import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

/**
 * Executa uma ação assíncrona e avisa o usuário do resultado — sucesso E falha.
 *
 * Existe porque o padrão anterior era `store.dispatch(...).then(() => useAlert('sucesso'))`
 * espalhado por 11 pontos do módulo de conversas: como as actions engoliam a
 * exceção, a promise resolvia mesmo com a API recusando, e a tela dizia
 * "status alterado" / "agente alterado" sem nada ter mudado no servidor.
 *
 * Com as actions propagando o erro (`throwErrorMessage`), este helper é o
 * único lugar que decide o que o usuário vê. Nunca relança: quem chama quis
 * um alerta, não um fluxo de exceção.
 *
 * @param {Promise} promise - a promise da action (já disparada)
 * @param {Object} [messages]
 * @param {string} [messages.success] - texto do alerta de sucesso (omitido = sem alerta)
 * @param {string} [messages.failure] - texto do alerta de falha (omitido = mensagem da API)
 * @returns {Promise<{ok: boolean, result?: *, error?: Error}>}
 *
 * @example
 * const { ok } = await dispatchWithAlert(
 *   store.dispatch('toggleStatus', payload),
 *   { success: t('CONVERSATION.CHANGE_STATUS'), failure: t('CONVERSATION.CHANGE_STATUS_FAILED') }
 * );
 */
export const dispatchWithAlert = async (promise, messages = {}) => {
  const { success, failure } = messages;

  try {
    const result = await promise;
    if (success) useAlert(success);
    return { ok: true, result };
  } catch (error) {
    useAlert(failure || String(parseAPIErrorResponse(error)));
    return { ok: false, error };
  }
};

export default dispatchWithAlert;
